import React, { useEffect, useState } from 'react';
import { Input, Typography } from 'antd';
import * as S from './styles';
import SystemPayRoutes from '../../utils/constants/Routes';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useAppDispatch } from '../../utils/hooks/redux';
import { STATUS_CODE } from '../../utils/constants/auth';
import { useNavigate } from 'react-router-dom';
import { resetPasswordThunkAsync } from '../../redux/features/auth/authAsyncThunk';

interface ResetPasswordProps {
  emailAddress?: string;
}

const ResetPassword = ({ emailAddress }: ResetPasswordProps) => {
  const [email, setEmail] = useState(emailAddress ?? '');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfPasswordVisible, setIsConfPasswordVisible] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !otp || !newPassword || !confirmNewPassword || newPassword !== confirmNewPassword) return;

    try {
      const resetPasswordParams = {
        email,
        newPassword,
        otp,
      };
      setIsSubmitting(true);
      const response: any = await dispatch(resetPasswordThunkAsync(resetPasswordParams));
      if (response?.payload?.statusCode === STATUS_CODE.SUCCESS && response?.payload?.success) {
        navigate(SystemPayRoutes.signIn);
      }
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (emailAddress) {
      setEmail(emailAddress);
    }
  }, [emailAddress]);

  return (
    <S.Container>
      <S.FormContainer>
        <Typography.Title level={2}>Reset Password</Typography.Title>
        <S.Form onSubmitCapture={handleSubmit}>
          <S.FormItem name="email" required normalize={(value) => value?.trim().toLowerCase() || ""}>
            <S.FormInput type="email" placeholder="Enter your email*" value={email} defaultValue={email} onChange={(e: any) => setEmail(e.target.value)} disabled />
          </S.FormItem>
          <S.FormItem name="otp" required rules={[{ required: true, message: 'Please enter OTP!' }]}>
            <S.FormInput type="otp" placeholder="Enter your OTP from your mail*" value={otp} onChange={(e: any) => setOtp(e.target.value)} />
          </S.FormItem>
          {/* Password Input with Show/Hide Eye Icon */}
          <S.FormItem name="newPassword" required rules={[{ required: true, message: 'Please enter a new password!' }]}>
            <Input
              name="newPassword"
              placeholder="Enter New Password*"
              type={isPasswordVisible ? 'text' : 'password'} // Toggle between 'text' and 'password'
              size="large"
              value={newPassword}
              onChange={(e: any) => setNewPassword(e.target.value)}
              suffix={isPasswordVisible ? <EyeInvisibleOutlined onClick={() => setIsPasswordVisible(false)} /> : <EyeOutlined onClick={() => setIsPasswordVisible(true)} />}
            />
          </S.FormItem>
          <S.FormItem
            name="confirmPassword"
            required
            rules={[
              { required: true, message: 'Please confirm your new password!' },
              ({ getFieldValue }: any) => ({
                validator(_: any, value: any) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Confirm password is not match with new password!'));
                },
              }),
            ]}
          >
            <Input
              name="confirmPassword"
              placeholder="Enter Confirm New Password"
              type={isConfPasswordVisible ? 'text' : 'password'} // Toggle between 'text' and 'password'
              size="large"
              value={confirmNewPassword}
              onChange={(e: any) => setConfirmNewPassword(e.target.value)}
              suffix={isConfPasswordVisible ? <EyeInvisibleOutlined onClick={() => setIsConfPasswordVisible(false)} /> : <EyeOutlined onClick={() => setIsConfPasswordVisible(true)} />}
            />
          </S.FormItem>
          <S.FormItem>
            <S.SubmitButton type="primary" htmlType="submit" loading={isSubmitting} disabled={isSubmitting} block>
              Reset Password
            </S.SubmitButton>
          </S.FormItem>
        </S.Form>
        <S.BackToLoginLink href={SystemPayRoutes.signIn}>Back to Login</S.BackToLoginLink>
      </S.FormContainer>
    </S.Container>
  );
};

export default ResetPassword;
